import type { User } from '$lib/shared/lib/types';
import type { RouteAccessOptions, RouteAccessResult, RoutePermission } from './types';
import { isActiveUser, isSuperuser, hasStreamAccess } from './types';
import { getNavigationState } from './navigation-state';
import { findMatchingRouteConfig } from './route-resolver';
import { UserNavigationStates } from './navigation-state';

export async function checkRouteAccess(
	path: string,
	user: User | null,
	options: RouteAccessOptions = {}
): Promise<RouteAccessResult> {
	const match = findMatchingRouteConfig(path);

	if (!match) {
		return {
			allowed: true,
			navigationState: getNavigationState(user, options.streams)
		};
	}

	const { config, params: routeParams } = match;
	const params = { ...routeParams, ...options.params };
	const navigationState = getNavigationState(user, options.streams);

	const deny = (redirect: string, reason: string): RouteAccessResult => ({
		allowed: false,
		redirect,
		reason,
		navigationState
	});

	if (config.public) {
		if (config.redirectIfAuth && user && !user.banned) {
			return deny(config.redirectIfAuth, 'Already authenticated');
		}
		return { allowed: true, navigationState };
	}

	const requiresAuth =
		config.requireAuth ||
		config.requireSuperuser ||
		config.requireActiveStreamer ||
		config.requireStreamAccess;

	if (requiresAuth && !user) {
		return deny('/signin', 'Authentication required');
	}

	if (config.requireNotBanned !== false && user?.banned) {
		return deny('/', 'Account is banned');
	}

	if (config.redirectIfComplete && user && isActiveUser(user)) {
		return deny(config.redirectIfComplete, 'Onboarding already complete');
	}

	if (config.allowedStates && config.allowedStates.length > 0) {
		if (!config.allowedStates.includes(navigationState)) {
			const redirect = getRedirectForState(navigationState, config);
			return deny(redirect, `Navigation state '${navigationState}' not allowed for this route`);
		}
	}

	if (config.requireSuperuser && !isSuperuser(user!)) {
		return deny('/admin', 'Superuser access required');
	}

	if (config.requireActiveStreamer && !isActiveUser(user!)) {
		return deny(
			config.redirectIfIncomplete || '/onboard',
			'Active streamer status required - complete onboarding'
		);
	}

	if (config.requireStreamAccess) {
		const tag = params.tag;

		// if (!tag) {
		// 	return deny('/admin', 'Stream tag parameter missing');
		// }
		if (tag !== undefined && !hasStreamAccess(user!, tag)) {
			return deny('/admin', `Access to show '${tag}' denied`);
		}
	}

	return { allowed: true, navigationState };
}

function getRedirectForState(state: string, config: RoutePermission): string {
	switch (state) {
		case UserNavigationStates.UNAUTHENTICATED:
			return '/signin';
		case UserNavigationStates.BANNED:
			return '/';
		case UserNavigationStates.NEW_USER:
		case UserNavigationStates.PENDING_APPROVAL:
		case UserNavigationStates.REJECTED:
			return config.redirectIfIncomplete || '/onboard';
		case UserNavigationStates.INACTIVE_USER:
		case UserNavigationStates.ACTIVE_USER:
		case UserNavigationStates.SUPERUSER:
			return config.redirectIfComplete || '/admin';
		case UserNavigationStates.VIEWER:
			return '/';
		default:
			return '/';
	}
}
